import { useEffect, useState } from 'react';

// graphql
import { PageInfo, useAdp_PlayersQuery, useEnableDisablePlayerMutation, useCheckPlayerBalanceMutation } from '@/generated/graphql';

// interfaces
import { IPlayerQueryVariable, IPlayerTable, mapGQLPlayer } from '@/shared/interfaces';

// helpers
import { formatNumber } from '@/shared/constants';

// components
import { TablePlayers } from './components/table';
import { ConfirmationModal, IModalConfirmationDetails } from '@/components/modal/confirmation';

export const PlayersPage = () => {
  const [enableDisablePlayerGQL] = useEnableDisablePlayerMutation();
  const [checkPlayerBalanceGQL] = useCheckPlayerBalanceMutation();

  const [players, setPlayers] = useState<IPlayerTable[]>([]);
  const [player, setPlayer] = useState<IPlayerTable | null>(null);
  const [confirmationModal, setConfirmationModal] = useState<IModalConfirmationDetails>({
    isOpen: false,
    title: 'Are you sure ?',
    messages: [],
  });

  const [playersQueryVariable, setPlayersQueryVariable] = useState<IPlayerQueryVariable>({
    first: 25,
  });
  const [playersPageInfo, setPlayersPageInfo] = useState<PageInfo>();
  const [playersTotalCount, setPlayersTotalCount] = useState<number>(0);

  const playersQuery = useAdp_PlayersQuery({
    variables: playersQueryVariable,
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const mappedData = data.players.nodes.map((x) => mapGQLPlayer(x));

      const mappedTableData: IPlayerTable[] = mappedData.map((x) => ({
        id: x.id,
        name: `${x.firstName} ${x.lastName}`,
        contactNumber: x.contactNumber,
        createdAt: x.createdAt,
        balanceFormatted: formatNumber(x.balance),
        username: x.user.username,
        isDisabled: x.user.isDisabled,
      }));

      setTimeout(() => {
        setPlayers(mappedTableData);
        setPlayersPageInfo(data.players.pageInfo);
        setPlayersTotalCount(data.players.totalCount);
      }, 100);
    },
  });

  const playerEvent = (data: IPlayerTable, eventName: string) => {
    if (eventName === 'enable-disable') {
      setPlayer(data);
      setConfirmationModal({
        isOpen: true,
        title: data.isDisabled ? 'Enable Player' : 'Disable Player',
        messages: [`Are you sure you want to ${data.isDisabled ? 'enable' : 'disable'} this player?`],
      });
    } else if (eventName === 'check balance') {
      checkPlayerBalanceGQL({
        variables: {
          playerId: data.id,
        },
        onCompleted(checkData) {
          if (checkData.checkPlayerBalance) {
            setConfirmationModal({
              isOpen: true,
              title: 'Balance is correct',
              messages: ['The player balance matches the expected amount.'],
              noActionBtns: true,
            });
          } else {
            setConfirmationModal({
              isOpen: true,
              title: 'Balance is incorrect',
              messages: ['The player balance does not match the expected amount. Please check the transactions.'],
              noActionBtns: true,
            });
          }
        },
        onError() {
          setConfirmationModal({
            isOpen: true,
            title: 'Error',
            messages: ['An error occurred while checking the player balance. Please try again later.'],
            noActionBtns: false,
          });
        },
      });
    }
  };

  const confirmEnableDisable = () => {
    if (player) {
      enableDisablePlayerGQL({
        variables: {
          input: {
            id: player.id,
            action: !player.isDisabled,
          },
        },
        onCompleted(data) {
          setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
          playersQuery.refetch();
        },
      });
    }
  };

  return (
    <>
      <TablePlayers
        rows={players}
        setPlayerQueryVariable={setPlayersQueryVariable}
        playerQueryVariable={playersQueryVariable}
        totalCount={playersTotalCount}
        pageInfo={playersPageInfo}
        playerEvent={playerEvent}
        getPlayerLoading={playersQuery.loading}
      />
      <ConfirmationModal
        modalDetails={confirmationModal}
        setModalDetails={setConfirmationModal}
        confirm={confirmEnableDisable}
        confirmText={'confirm'}
      />
    </>
  );
};
